import { useCallback, useReducer } from 'react'

// @i18n-ignore-start
export const SORT_BY = {
  HIGHEST_RATING: 'HIGHEST_RATING',
  LOWEST_RATING: 'LOWEST_RATING',
  NEWEST: 'NEWEST',
  OLDEST: 'OLDEST',
  DEFAULT: '',
} as const

export const SORT_BY_OPTIONS_MAP = {
  [SORT_BY.HIGHEST_RATING]: { sortBy: 'rating', sortOrder: 'DESC' },
  [SORT_BY.NEWEST]: { sortBy: 'createdDate', sortOrder: 'DESC' },
  [SORT_BY.LOWEST_RATING]: { sortBy: 'rating', sortOrder: 'ASC' },
  [SORT_BY.OLDEST]: { sortBy: 'createdDate', sortOrder: 'ASC' },
  [SORT_BY.DEFAULT]: { sortBy: 'rating', sortOrder: 'DESC' },
}
// @i18n-ignore-end

type SORT_BY_KEYS = keyof typeof SORT_BY
type SORT_BY_TYPES = (typeof SORT_BY)[SORT_BY_KEYS]

interface FilterState {
  page: number
  sortBy: SORT_BY_TYPES
  hasPhoto: boolean
}
enum ACTION_TYPES {
  SET_PAGE = 'SET_PAGE',
  SET_SORT = 'SET_SORT',
  SET_HAS_PHOTOS = 'SET_HAS_PHOTOS',
}

type Action =
  | {
      type: ACTION_TYPES.SET_HAS_PHOTOS
      hasPhoto: boolean
    }
  | {
      type: ACTION_TYPES.SET_SORT
      sortBy: SORT_BY_TYPES
    }
  | {
      type: ACTION_TYPES.SET_PAGE
      page: number
    }

const reducer = (state: FilterState, action: Action) => {
  const nextState = { ...state }
  switch (action.type) {
    case ACTION_TYPES.SET_HAS_PHOTOS:
      nextState.hasPhoto = action.hasPhoto
      nextState.page = 1
      break
    case ACTION_TYPES.SET_PAGE:
      nextState.page = action.page
      break
    case ACTION_TYPES.SET_SORT:
      nextState.sortBy = action.sortBy
      nextState.page = 1
      break
  }
  return nextState
}

const DEFAULT_FILTER_STATE = {
  page: 1,
  sortBy: SORT_BY.DEFAULT,
  hasPhoto: false,
}
function useFilterState() {
  const [{ hasPhoto, page, sortBy }, dispatch] = useReducer(reducer, DEFAULT_FILTER_STATE)

  const setHasPhoto = useCallback(
    (hasPhoto: boolean) => {
      dispatch({
        type: ACTION_TYPES.SET_HAS_PHOTOS,
        hasPhoto,
      })
    },
    [dispatch],
  )
  const setSortBy = useCallback(
    (sortBy: string) => {
      dispatch({
        type: ACTION_TYPES.SET_SORT,
        sortBy: sortBy as SORT_BY_TYPES,
      })
    },
    [dispatch],
  )

  const setPage = useCallback(
    (page: number) => {
      dispatch({
        type: ACTION_TYPES.SET_PAGE,
        page,
      })
    },
    [dispatch],
  )

  return {
    setHasPhoto,
    setSortBy,
    setPage,
    hasPhoto,
    page,
    sortBy,
  }
}

export default useFilterState
